import type { NuxtApp } from '#app'
import { clearNuxtData, refreshNuxtData, useNuxtApp } from '#app'
import type { AsyncDataRequestStatus, _AsyncData } from '#app/composables/asyncData'
import { QUERY_CACHE_KEY, useQueryCache } from '#imports'
import { runPrefetch } from '../../utils/prefetch'

export type QueryFilter = {
    /**
     * Filter by key. Will match any queries where its key starts with the filter.
     * In order to filter more specifically, combine it with the `exact` property,
     * where only query keys that exactly match the `key` filter is returned.
     */
    key?: string

    /**
     * Exactly match keys
     */
    exact?: boolean

    /**
     * Filter by query status
     */
    status?: AsyncDataRequestStatus

    /**
     * Filter queries based on whether they are currently used.
     *
     * @description
     * - `active`: Queries that have 1 or more subscribers i.e. they are actively used on the page.
     * - `inactive`: Queries with 0 subscribers i.e. queries that are not actively used but have been registered previously.
     * - `all`: Will match all queries and disregard whether they are used or not.
     *
     * @default active
     */
    type?: 'active' | 'inactive' | 'all'

    /**
     * Filter by a custom predicate. This filter will run last, if multiple filters are enabled.
     */
    predicate?: (key: string, query: NonNullable<NuxtApp['_asyncData'][string]>) => boolean
}

/** Ceiling on the number of inactive queries this layer keeps cached. */
export const MAX_INACTIVE_QUERIES = 50

export type QueryKey = string
export type QueryResult = _AsyncData<any, any>

/**
 * A tuple of key and AsyncData
 */
export type QueryMatch = [QueryKey, QueryResult]

export const useQueryClient = () => {
    const nuxtApp = useNuxtApp()
    const queryCache = useQueryCache()

    function isActive(key: string): boolean {
        const deps = nuxtApp._asyncData?.[key]?._deps
        if (!deps) return false

        return deps >= 1
    }

    function matchQueries(filters: QueryFilter = {
        type: 'active'
    }): QueryMatch[] {
        const queries = Object.entries(nuxtApp.payload.data)

        return queries
            // The cache's own stamps sit in `payload.data` with no `_asyncData`, matching every filter.
            .filter(([key]) => key !== QUERY_CACHE_KEY)
            .filter(([key]) => {
                if (!filters.key) return true

                if (filters.exact) return key === filters.key

                return key.startsWith(filters.key)
            })
            .filter(([key]) => {
                const filter = filters.type ?? 'active'
                if (!filter || filter === 'all') return true

                const asyncDataQuery = nuxtApp._asyncData?.[key]
                if (!asyncDataQuery) return true

                const _isActive = isActive(key)

                if (filter === 'active') return _isActive
                if (filter === 'inactive') return !_isActive

                return false
            })
            .filter(([_, query]) => {
                if (!filters.status) return true

                return query.status.value === filters.status
            })
            .filter(([key, query]) => {
                if (!filters.predicate) return true

                return filters.predicate(key, query!)
            })
    }

    /**
     * Remove queries that match the filter.
     *
     * @param filters Filter which queries to remove or don't set it to remove all queries.
     */
    function removeQueries(filters?: QueryFilter) {
        const queries = matchQueries(filters)

        release(queries.map(([key]) => key))
    }

    function release(keys: string[]) {
        queryCache.forget(keys)
        clearNuxtData(keys)
    }

    /**
     * Release inactive queries this data layer cached, bounding what a long session
     * retains. Two rules, in order:
     *
     * 1. an inactive query whose cached data `useQueryCache` would no longer serve is
     *    released — holding bytes it refuses to hand out buys nothing;
     * 2. if more than `MAX_INACTIVE_QUERIES` inactive queries survive that, the oldest are
     *    released until the ceiling holds. `payload.data` enumerates in creation order, the
     *    only recency signal this layer records, so "oldest" means first created. Past the
     *    ceiling, back navigation to one of those routes refetches.
     *
     * Active queries are never released, however stale, and neither is async data this
     * layer never cached — a `useAsyncData` caller outside `useQuery` keeps its own data.
     */
    function releaseInactiveQueries() {
        const inactive = matchQueries({ type: 'inactive' })
            .map(([key]) => key)
            .filter(key => queryCache.owns(key))

        const unserveable: string[] = []
        const serveable: string[] = []

        for (const key of inactive) {
            if (queryCache.get(key) === undefined) unserveable.push(key)
            else serveable.push(key)
        }

        release(unserveable)

        if (serveable.length > MAX_INACTIVE_QUERIES) {
            release(serveable.slice(0, serveable.length - MAX_INACTIVE_QUERIES))
        }
    }

    /**
     * Refetch queries that match the filter.
     *
     * @param filters Filter which queries to refetch or don't set it to refetch all queries.
     *
     * @return void
     */
    function refetchQueries(filters: QueryFilter = {}) {
        const queries = matchQueries({
            type: 'active',
            ...filters
        })

        const keys = queries.map(([key]) => key)

        return refreshNuxtData(keys)
    }

    /**
     * Invalidate queries that match the filter.
     *
     * @description
     * - Active queries will get their cache cleared and refetched
     * - Inactive queries will get their cache cleared, so they will be refecthed next time they are used
     *
     * @returns Promise
     */
    function invalidateQueries(filters: Omit<QueryFilter, 'type'> = {}) {
        const queries = matchQueries({
            ...filters,
            type: 'all'
        })

        const activeKeys: string[] = []
        const inactiveKeys: string[] = []

        queries.forEach(([key]) => {
            const _isActive = isActive(key)

            if (_isActive) {
                activeKeys.push(key)
            }
            else {
                inactiveKeys.push(key)
            }
        })

        queryCache.unset([...activeKeys, ...inactiveKeys])

        return refreshNuxtData(activeKeys)
    }

    /**
     * Get a query by key.
     *
     * @param key A query key
     *
     * @returns A query object (equal to what useQuery returns)
     */
    function getQueryState(key: string): NuxtApp['_asyncData'][string] {
        const query = nuxtApp._asyncData?.[key]

        return query
    }

    /** Starts and returns the requests of the queries `declare` calls, for the page's own queries on those keys to take over. */
    function prefetchQueries(declare: () => void): Promise<unknown>[] {
        return runPrefetch(nuxtApp, declare)
    }

    return {
        prefetchQueries,
        matchQueries,
        removeQueries,
        releaseInactiveQueries,
        refetchQueries,
        invalidateQueries,
        getQueryState
    }
}
